import * as React from 'react';

import classNames from 'classnames';
import {AnimatePresence, motion} from 'framer-motion';

import {colors} from '../colors/colors';
import {LoadingIcon} from '../icon/svg/loading-icon';

import styles from './saved-setting-toast.module.css';

export type SavedSettingToastState = 'saving' | 'complete' | 'initial';

type Props = {
    children?: React.ReactNode;
    state: SavedSettingToastState;
    toastStyles?: any;
    savedText: string;
    onStateChange?: (newState: SavedSettingToastState) => void;
    autoResetDelay?: number;
    largerSpinner?: boolean;
};

const spinnerVariants = {
    visible: {opacity: 1},
    hidden: {opacity: 0},
} as const;

export function SavedSettingToast(props: Props) {
    const timeoutRef = React.useRef<number | null | undefined>(null);

    React.useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (props.state === 'complete' && props.onStateChange) {
            // @ts-expect-error
            timeoutRef.current = setTimeout(() => {
                if (props.onStateChange) {
                    props.onStateChange('initial');
                }
                timeoutRef.current = null;
            }, props.autoResetDelay);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.state, props.onStateChange]);

    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);

    const spinnerStyleNames = classNames({
        [styles.spinner]: !props.largerSpinner,
        [styles['larger-spinner']]: props.largerSpinner,
    });

    return (
        <div className='flex align-center'>
            {props.children}
            <motion.div
                style={props.toastStyles}
                aria-hidden={true}
                initial='hidden'
                animate={props.state === 'saving' ? 'visible' : 'hidden'}
                variants={spinnerVariants}
                transition={{duration: 0.2}}
                className={spinnerStyleNames}
            >
                <LoadingIcon size={null} wrapWithDiv={false} fill={colors.green} />
            </motion.div>
            <AnimatePresence>
                {props.state === 'complete' && (
                    <motion.div
                        key='complete'
                        style={props.toastStyles}
                        initial={{y: 0, x: props.children ? -10 : -5, opacity: 0}}
                        animate={{y: 0, x: props.children ? -5 : -10, opacity: 1}}
                        exit={{y: -10, x: props.children ? -5 : -10, opacity: 0}}
                        transition={{duration: 0.2}}
                        className={styles['save-complete']}
                    >
                        {props.savedText}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

SavedSettingToast.defaultProps = {
    savedText: 'Saved',
    autoResetDelay: 2000,
};
